import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { ColorV2 } from "config/colors";
import { TypographyV2 } from "config/font";
import { SecondaryButton } from "components/designV2/buttons/secondaryButton";
import { PrimaryButton } from "components/designV2/buttons/primaryButton";
import { CustomIcon } from "components/icon/customIcon.component";
import { useTranslation } from "react-i18next";

export type ModalSize = "small" | "medium" | "large";

export interface ModalProps {
  label: string;
  size?: ModalSize;
  showCloseButton?: boolean;
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
  footer?: React.ReactNode;
  iconName?: string;
  variant?: "default" | "confirm";
  confirmOnClose?: boolean;
  confirmText?: string;
  confirmLabel?: string;
}

const SIZE_MAP: Record<ModalSize, { width: number; minHeight: number }> = {
  small: { width: 520, minHeight: 520 },
  medium: { width: 1000, minHeight: 650 },
  large: { width: 1000, minHeight: 880 },
};

const DEFAULT_CONFIRM_LABEL_KEY = "DesignComponents.Close";
const DEFAULT_CONFIRM_TEXT_KEY = "DesignComponents.ConfirmCloseBody";
const DEFAULT_CONFIRM_TITLE_KEY = "DesignComponents.ConfirmCloseTitle";

let modalId = 0;

/** Modal shell with header, content, and optional footer. */
export const Modal = ({
  label,
  size = "medium",
  showCloseButton = true,
  variant = "default",
  iconName,
  confirmOnClose = false,
  confirmText,
  confirmLabel,
  isOpen,
  onClose,
  children,
  footer,
}: ModalProps): JSX.Element | null => {
  const sizeConfig = SIZE_MAP[size];
  const isConfirm = variant === "confirm";
  const titleIdRef = useRef<string>();
  const contentIdRef = useRef<string>();
  const confirmTitleIdRef = useRef<string>();
  if (!titleIdRef.current || !contentIdRef.current) {
    modalId += 1;
    titleIdRef.current = `modal-title-${modalId}`;
    contentIdRef.current = `modal-content-${modalId}`;
    confirmTitleIdRef.current = `modal-confirm-title-${modalId}`;
  }
  const titleId = titleIdRef.current;
  const contentId = contentIdRef.current;
  const confirmTitleId = confirmTitleIdRef.current;
  const cardRef = useRef<HTMLDivElement | null>(null);
  const confirmCardRef = useRef<HTMLDivElement | null>(null);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const { t } = useTranslation();

  useEffect(() => {
    if (!isOpen) return;
    const focusable = cardRef.current?.querySelector<HTMLElement>(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
    );
    focusable?.focus();
  }, [isOpen]);

  useEffect(() => {
    if (!isConfirmOpen) return;
    const focusable = confirmCardRef.current?.querySelector<HTMLElement>(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
    );
    focusable?.focus();
  }, [isConfirmOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (event: KeyboardEvent): void => {
      if (event.key !== "Escape") return;
      event.stopPropagation();
      if (isConfirmOpen) {
        setIsConfirmOpen(false);
        return;
      }
      if (confirmOnClose) {
        setIsConfirmOpen(true);
        return;
      }
      onClose();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [confirmOnClose, isConfirmOpen, isOpen, onClose]);

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>): void => {
    if (event.key !== "Tab") return;
    const focusables = cardRef.current?.querySelectorAll<HTMLElement>(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
    );
    if (!focusables || focusables.length === 0) return;
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  const handleConfirmKeyDown = (
    event: React.KeyboardEvent<HTMLDivElement>,
  ): void => {
    if (event.key !== "Tab") return;
    const focusables = confirmCardRef.current?.querySelectorAll<HTMLElement>(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
    );
    if (!focusables || focusables.length === 0) return;
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  if (!isOpen) return null;

  const handleOverlayClick = (): void => {
    if (isConfirmOpen) return;
    if (confirmOnClose) {
      setIsConfirmOpen(true);
      return;
    }
    onClose();
  };

  const handleCardClick = (event: React.MouseEvent<HTMLDivElement>): void => {
    event.stopPropagation();
  };

  const handleRequestClose = (): void => {
    if (confirmOnClose) {
      setIsConfirmOpen(true);
      return;
    }
    onClose();
  };

  const handleConfirmClose = (): void => {
    setIsConfirmOpen(false);
    onClose();
  };

  const handleCancelConfirm = (): void => {
    setIsConfirmOpen(false);
  };

  const handleConfirmOverlayClick = (
    event: React.MouseEvent<HTMLDivElement>,
  ): void => {
    event.stopPropagation();
    setIsConfirmOpen(false);
  };

  return (
    <Overlay
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      onClick={handleOverlayClick}
    >
      <Card
        ref={cardRef}
        $width={sizeConfig.width}
        $minHeight={sizeConfig.minHeight}
        onKeyDown={handleKeyDown}
        onClick={handleCardClick}
        aria-describedby={contentId}
      >
        {isConfirm ? (
          <>
            <ConfirmHeader>
              {iconName && (
                <ConfirmIcon>
                  <CustomIcon
                    name={iconName}
                    size="144px"
                    color={ColorV2.Blue4}
                  />
                </ConfirmIcon>
              )}
              <Title id={titleId} $centered>
                {label}
              </Title>
            </ConfirmHeader>
            <Content id={contentId} $centered $confirm>
              <ConfirmText>{children}</ConfirmText>
            </Content>
            {footer && <Footer $centered>{footer}</Footer>}
          </>
        ) : (
          <>
            <Header>
              <Title id={titleId}>{label}</Title>
              {showCloseButton && (
                <SecondaryButton
                  iconName="v2/cross"
                  aria-label={t("DesignComponents.CloseModal")}
                  size="small"
                  onClick={handleRequestClose}
                />
              )}
            </Header>
            <Content id={contentId}>{children}</Content>
            {footer && <Footer>{footer}</Footer>}
          </>
        )}
      </Card>
      {isConfirmOpen && (
        <ConfirmOverlay onClick={handleConfirmOverlayClick}>
          <ConfirmCard
            ref={confirmCardRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby={confirmTitleId}
            onClick={handleCardClick}
            onKeyDown={handleConfirmKeyDown}
          >
            <ConfirmTitle id={confirmTitleId}>
              {t(DEFAULT_CONFIRM_TITLE_KEY)}
            </ConfirmTitle>
            <ConfirmBody>
              {confirmText ?? t(DEFAULT_CONFIRM_TEXT_KEY)}
            </ConfirmBody>
            <ConfirmActions>
              <SecondaryButton
                label={t("DesignComponents.Cancel")}
                onClick={handleCancelConfirm}
              />
              <PrimaryButton
                label={confirmLabel ?? t(DEFAULT_CONFIRM_LABEL_KEY)}
                onClick={handleConfirmClose}
              />
            </ConfirmActions>
          </ConfirmCard>
        </ConfirmOverlay>
      )}
    </Overlay>
  );
};

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.45);
  z-index: 1100;
  padding: 32px;
`;

const Card = styled.div<{ $width: number; $minHeight: number }>`
  width: min(100%, ${(props) => props.$width}px);
  min-height: ${(props) => props.$minHeight}px;
  max-height: calc(100vh - 64px);
  background: ${ColorV2.White};
  border-radius: 16px;
  border: 1.5px solid ${ColorV2.Blue5};
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.25);
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const Header = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding: 32px 24px 0;
  gap: 16px;
`;

const Title = styled.h3<{ $centered?: boolean }>`
  ${TypographyV2.h3};
  color: ${ColorV2.Blue1};
  margin: 10px 0 0;
  text-align: ${(props) => (props.$centered ? "center" : "left")};
`;

const Content = styled.div<{ $centered?: boolean; $confirm?: boolean }>`
  padding: ${(props) => (props.$confirm ? "16px 72px 24px" : "16px 24px 24px")};
  display: flex;
  flex-direction: column;
  gap: 16px;
  flex: 1;
  overflow-y: auto;
  text-align: ${(props) => (props.$centered ? "center" : "left")};
  align-items: ${(props) => (props.$centered ? "center" : "stretch")};
`;

const Footer = styled.div<{ $centered?: boolean }>`
  padding: 16px 24px 24px;
  display: flex;
  justify-content: ${(props) => (props.$centered ? "center" : "flex-end")};
  gap: 12px;
  margin-top: auto;
`;

const ConfirmHeader = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 32px 24px 0;
  gap: 16px;
`;

const ConfirmIcon = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  opacity: 0.5;
`;

const ConfirmText = styled.div`
  ${TypographyV2.b3};
  line-height: 33px;
  color: ${ColorV2.Blue4};
  text-align: center;
  max-width: 520px;
`;

const ConfirmOverlay = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.2);
  padding: 24px;
`;

const ConfirmCard = styled.div`
  width: min(100%, 420px);
  background: ${ColorV2.White};
  border-radius: 16px;
  border: 1.5px solid ${ColorV2.Blue5};
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  text-align: center;
`;

const ConfirmTitle = styled.div`
  ${TypographyV2.h4};
  color: ${ColorV2.Blue1};
`;

const ConfirmBody = styled.div`
  ${TypographyV2.b2};
  color: ${ColorV2.Blue4};
`;

const ConfirmActions = styled.div`
  display: flex;
  justify-content: center;
  gap: 12px;
`;
